import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AiReportSections } from './AiReportSections';
import { ArrivalsSection, IdleSection } from './ReportIndicators';
import { InsightBadges } from '../components/InsightBadges';
import { duration, Stat } from '../components/Stat';
import { TeamBadge } from '../components/TeamBadge';
import { toast } from '../components/toasts';
import { Button, Empty, ErrorText, Input, Select, Spinner } from '../components/ui';
import { errorMessage, fetchBlobUrl, get } from '../lib/api';
import { dateTimeOf, formatPhone } from '../lib/format';
import { useOperators } from '../lib/queries';
import { ReportAttendanceRow, ReportSummary, Team } from '../lib/types';

const TZ = 'America/Cuiaba';
/** AAAA-MM-DD no fuso da empresa. */
const dayOf = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: TZ });
const addDays = (day: string, n: number) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const br = (day: string) => day.split('-').reverse().join('/');

function presets(): Array<{ label: string; from: string; to: string }> {
  const today = dayOf(new Date());
  const [y, m] = today.split('-').map(Number);
  const firstOfMonth = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastMonthEnd = addDays(firstOfMonth, -1);
  return [
    { label: 'Hoje', from: today, to: today },
    { label: 'Ontem', from: addDays(today, -1), to: addDays(today, -1) },
    { label: '7 dias', from: addDays(today, -6), to: today },
    { label: '30 dias', from: addDays(today, -29), to: today },
    { label: 'Este mês', from: firstOfMonth, to: today },
    { label: 'Mês passado', from: `${lastMonthEnd.slice(0, 7)}-01`, to: lastMonthEnd },
  ];
}

const CLOSING_TONE: Record<string, string> = { finalizado: 'text-ok', cancelado: 'text-fg-soft', spam: 'text-danger' };
/** Papel do operador filtrado quando não é o responsável. */
const ROLE_LABEL: Record<string, string> = { transferiu: 'transferiu', ajudou: 'ajudou', anterior: 'atendeu antes' };

/** Ligações do período: totais e por operador (a equipe não se aplica: ligação não tem equipe). */
function CallsSection({ summary: s, teamFiltered }: { summary: ReportSummary; teamFiltered: boolean }) {
  const c = s.calls;
  const pct = (n: number, of: number) => (of ? ` (${Math.round((n / of) * 100)}%)` : '');
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Ligações</h2>
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Stat label="Recebidas" value={c.received} />
        <Stat label="Atendidas" value={`${c.answered}${pct(c.answered, c.received)}`} tone="ok" />
        <Stat label="Perdidas" value={`${c.missed}${pct(c.missed, c.received)}`} tone={c.missed ? 'warn' : undefined} hint="O cliente ligou e ninguém atendeu" />
        <Stat label="Sem retorno" value={c.missedWithoutReturn} tone={c.missedWithoutReturn ? 'warn' : undefined} hint="Perdidas sem ligação de volta e sem nova ligação atendida do mesmo número" />
        <Stat label="Espera média na fila" value={duration(c.avgWaitSeconds)} hint={`Maior espera: ${duration(c.maxWaitSeconds)}`} />
        <Stat label="Feitas" value={c.made} hint={`${c.madeAnswered} atendidas pelo cliente`} />
        <Stat label="Em conversa" value={duration(c.talkSeconds)} hint="Soma do atender ao desligar (o tempo de atendimento é o das sessões)" />
      </div>
      <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm lg:w-1/2">
        <thead className="bg-muted text-left text-xs text-fg-soft">
          <tr>
            <th className="px-3 py-2">Operador</th>
            <th className="px-3 py-2 text-right">Atendidas</th>
            <th className="px-3 py-2 text-right">Feitas</th>
            <th className="px-3 py-2 text-right" title="Do atender ao desligar">Em conversa</th>
          </tr>
        </thead>
        <tbody>
          {s.callsByOperator.map((o) => (
            <tr key={o.operatorId} className="border-t border-border">
              <td className="px-3 py-2">{o.name}</td>
              <td className="px-3 py-2 text-right tabular-nums">{o.answered}</td>
              <td className="px-3 py-2 text-right tabular-nums">{o.made}</td>
              <td className="px-3 py-2 text-right font-mono text-xs">{duration(o.talkSeconds)}</td>
            </tr>
          ))}
          {s.callsByOperator.length === 0 && (
            <tr>
              <td colSpan={4} className="px-3 py-3 text-center text-fg-soft">Nenhuma ligação atendida ou feita no período.</td>
            </tr>
          )}
        </tbody>
      </table>
      {teamFiltered && <p className="mt-1 text-[11px] text-fg-soft">O filtro de equipe não se aplica às ligações.</p>}
    </section>
  );
}

/** Relatórios por período (supervisão): totais, por dia, por equipe, por operador e a lista com exportação. */
export function ReportsPage({ onOpen }: { onOpen: (attendanceId: string) => void }) {
  const options = useMemo(presets, []);
  const [from, setFrom] = useState(options[3].from);
  const [to, setTo] = useState(options[3].to);
  const [teamId, setTeamId] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const [exporting, setExporting] = useState<'attendances' | 'calls' | null>(null);
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => get<Team[]>('/teams') });
  const operators = useOperators();
  const operatorName = operators.data?.find((o) => o.id === operatorId)?.name.split(' ')[0] ?? 'operador';

  const qs = new URLSearchParams({ from, to, ...(teamId && { teamId }), ...(operatorId && { operatorId }) }).toString();
  const valid = Boolean(from && to && from <= to);
  const summary = useQuery({ queryKey: ['report-summary', qs], queryFn: () => get<ReportSummary>(`/reports/summary?${qs}`), enabled: valid });
  const rows = useQuery({ queryKey: ['report-rows', qs], queryFn: () => get<ReportAttendanceRow[]>(`/reports/attendances?${qs}`), enabled: valid });

  const exportCsv = async (kind: 'attendances' | 'calls') => {
    setExporting(kind);
    try {
      const url = await fetchBlobUrl(`/reports/${kind}.csv?${qs}`);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind === 'calls' ? 'ligacoes' : 'atendimentos'}_${from}_${to}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setExporting(null);
    }
  };

  const s = summary.data;
  const maxDay = Math.max(1, ...(s?.byDay.map((d) => d.total) ?? [1]));

  return (
    <div className="mx-auto h-full max-w-6xl overflow-y-auto p-3 md:p-6">
      <h1 className="mb-3 text-lg font-semibold">Relatórios</h1>

      {/* Celular: grade de duas colunas (datas, equipe/operador e os dois CSV lado a lado). */}
      <div className="mb-4 grid grid-cols-2 items-end gap-2 rounded-lg border border-border bg-panel p-3 md:flex md:flex-wrap">
        <div className="col-span-2 flex flex-wrap gap-1">
          {options.map((p) => (
            <button
              key={p.label}
              className={clsx('rounded-md px-2.5 py-1 text-xs', p.from === from && p.to === to ? 'bg-accent/10 font-medium text-accent' : 'text-fg-soft hover:bg-muted')}
              onClick={() => {
                setFrom(p.from);
                setTo(p.to);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label className="text-xs text-fg-soft">
          De
          <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="w-full md:w-36" />
        </label>
        <label className="text-xs text-fg-soft">
          Até
          <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="w-full md:w-36" />
        </label>
        <label className="text-xs text-fg-soft">
          Equipe
          <Select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="w-full md:w-40">
            <option value="">Todas</option>
            {(teams.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            <option value="none">Sem equipe</option>
          </Select>
        </label>
        <label className="text-xs text-fg-soft">
          Operador
          <Select value={operatorId} onChange={(e) => setOperatorId(e.target.value)} className="w-full md:w-44">
            <option value="">Todos</option>
            {(operators.data ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </label>
        <Button className="md:ml-auto" onClick={() => void exportCsv('attendances')} loading={exporting === 'attendances'} disabled={!valid}>
          <Download className="size-4" /> Atendimentos<span className="hidden sm:inline"> (CSV)</span>
        </Button>
        <Button onClick={() => void exportCsv('calls')} loading={exporting === 'calls'} disabled={!valid}>
          <Download className="size-4" /> Ligações<span className="hidden sm:inline"> (CSV)</span>
        </Button>
      </div>

      {!valid && <p className="text-sm text-warn">A data final precisa ser igual ou depois da inicial.</p>}
      <ErrorText error={summary.error} />
      {summary.isLoading && <Spinner />}

      {s && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
            <Stat label="Atendimentos" value={s.totals.total} />
            <Stat label="Recebidos" value={s.totals.received} />
            <Stat label="Iniciados por nós" value={s.totals.startedByOperator} />
            <Stat label="Finalizados" value={s.totals.finished} tone="ok" />
            <Stat label="Cancelados / spam" value={s.totals.cancelled + s.totals.spam} />
            <Stat label="Espera média" value={duration(s.totals.avgWaitSeconds)} hint="Da chegada do cliente até um operador abrir o atendimento" />
            <Stat label="1ª resposta média" value={duration(s.totals.avgFirstResponseSeconds)} hint="Da chegada do cliente até a primeira mensagem de um operador" />
            <Stat label="Tempo efetivo médio" value={duration(s.totals.avgEffectiveSeconds)} hint="Soma das sessões de foco, nos finalizados" />
          </div>

          {s.byDay.length > 1 && (
            <section className="mb-6">
              <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Por dia</h2>
              <div className="flex h-36 items-end gap-1 rounded-lg border border-border bg-panel p-3">
                {s.byDay.map((d) => (
                  <div key={d.day} className="group flex h-full min-w-0 flex-1 flex-col items-center justify-end" title={`${br(d.day)}: ${d.total} atendimentos, ${d.finished} finalizados`}>
                    <span className="mb-0.5 text-[10px] text-fg-soft opacity-0 group-hover:opacity-100">{d.total}</span>
                    <div className="w-full max-w-8 rounded-t bg-accent/80" style={{ height: `${(d.total / maxDay) * 100}%`, minHeight: d.total ? 2 : 0 }} />
                    <span className="mt-1 truncate text-[10px] text-fg-soft">{d.day.slice(8)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <section>
              <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Por equipe</h2>
              <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
                <thead className="bg-muted text-left text-xs text-fg-soft">
                  <tr>
                    <th className="px-3 py-2">Equipe</th>
                    <th className="px-3 py-2 text-right">Atend.</th>
                    <th className="px-3 py-2 text-right">Finaliz.</th>
                    <th className="px-3 py-2 text-right">Espera</th>
                    <th className="px-3 py-2 text-right">Tempo médio</th>
                  </tr>
                </thead>
                <tbody>
                  {s.byTeam.map((x) => (
                    <tr key={x.teamId ?? 'none'} className="border-t border-border">
                      <td className="px-3 py-2">{x.team ? <TeamBadge name={x.team} /> : <TeamBadge name={null} showEmpty />}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{x.total}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{x.finished}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{duration(x.avgWaitSeconds)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{duration(x.avgEffectiveSeconds)}</td>
                    </tr>
                  ))}
                  {s.byTeam.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-3 text-center text-fg-soft">Sem atendimentos no período.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
            <section>
              <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Por operador</h2>
              <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
                <thead className="bg-muted text-left text-xs text-fg-soft">
                  <tr>
                    <th className="px-3 py-2">Operador</th>
                    <th className="px-3 py-2 text-right" title="Soma das sessões de foco no período">Tempo efetivo</th>
                    <th className="px-3 py-2 text-right" title="Atendimentos em que trabalhou (abriu na tela)">Trab.</th>
                    <th className="px-3 py-2 text-right">Finaliz.</th>
                    <th className="px-3 py-2 text-right">Mensagens</th>
                  </tr>
                </thead>
                <tbody>
                  {s.byOperator.map((o) => (
                    <tr key={o.operatorId} className="border-t border-border">
                      <td className="px-3 py-2">{o.name}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{duration(o.effectiveSeconds)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{o.attendancesWorked}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{o.finished}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{o.messagesSent}</td>
                    </tr>
                  ))}
                  {s.byOperator.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-3 text-center text-fg-soft">Sem atividade no período.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {teamId && <p className="mt-1 text-[11px] text-fg-soft">O filtro de equipe não se aplica aqui: um operador atende várias equipes.</p>}
            </section>
          </div>

          <ArrivalsSection slots={s.arrivals} from={from} to={to} teamFiltered={Boolean(teamId)} />
          <IdleSection rows={s.idle} teamFiltered={Boolean(teamId)} />

          {s.calls.received + s.calls.made > 0 && <CallsSection summary={s} teamFiltered={Boolean(teamId)} />}
        </>
      )}

      {valid && <AiReportSections from={from} to={to} onOpen={onOpen} />}

      <section>
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-xs font-semibold tracking-wide text-fg-soft uppercase">Atendimentos</h2>
          {rows.data && rows.data.length >= 500 && <span className="text-xs text-fg-soft">mostrando os 500 mais recentes; a exportação traz todos</span>}
        </div>
        {operatorId && (
          <p className="mb-2 text-xs text-fg-soft">
            Com operador escolhido, a lista inclui também os atendimentos em que ele ajudou, que transferiu ou que foram abertos antes do
            período. <strong>Tempo dele</strong> soma só as sessões dele no período e fecha com a tabela Por operador; os quadros do topo
            contam só os atendimentos de que ele é o responsável.
          </p>
        )}
        <ErrorText error={rows.error} />
        {rows.data && rows.data.length === 0 && <Empty>Nenhum atendimento no período.</Empty>}
        {rows.data && rows.data.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full bg-panel text-sm">
              <thead className="bg-muted text-left text-xs text-fg-soft">
                <tr>
                  <th className="px-3 py-2">Início</th>
                  <th className="px-3 py-2">Contato</th>
                  <th className="px-3 py-2">Equipe</th>
                  <th className="px-3 py-2">Operador</th>
                  <th className="px-3 py-2">Encerramento</th>
                  <th className="px-3 py-2">Chamado</th>
                  <th className="px-3 py-2 text-right">Espera</th>
                  <th className="px-3 py-2 text-right">1ª resposta</th>
                  <th className="px-3 py-2 text-right" title="Soma das sessões de todos os operadores no atendimento">Tempo efetivo</th>
                  {operatorId && (
                    <th className="px-3 py-2 text-right" title="Só as sessões do operador escolhido, no período (soma igual à tabela Por operador)">
                      Tempo dele
                    </th>
                  )}
                  <th className="px-3 py-2" title="Urgência e humor pela análise da IA">IA</th>
                </tr>
              </thead>
              <tbody>
                {rows.data.map((r) => (
                  <tr key={r.id} className="cursor-pointer border-t border-border hover:bg-muted/50" onClick={() => onOpen(r.id)} title="Abrir o atendimento">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {dateTimeOf(r.createdAt)}
                      {r.origin === 'operador' && <span className="ml-1 text-[10px] text-fg-soft" title="Iniciado por um operador">↗</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-48 truncate">{r.contact ?? '—'}</div>
                      <div className="text-xs text-fg-soft">{r.phone ? formatPhone(r.phone) : ''}</div>
                    </td>
                    <td className="px-3 py-2">{r.team ? <TeamBadge name={r.team} /> : '—'}</td>
                    <td className="px-3 py-2">
                      {r.operator ?? '—'}
                      {r.operatorRole && ROLE_LABEL[r.operatorRole] && (
                        <div className="text-xs text-fg-soft" title="Papel do operador escolhido no filtro">
                          {operatorName}: {ROLE_LABEL[r.operatorRole]}
                        </div>
                      )}
                    </td>
                    <td className={clsx('px-3 py-2', r.closing && CLOSING_TONE[r.closing])}>{r.closing ?? <span className="text-warn">em aberto</span>}</td>
                    <td className="px-3 py-2">{r.ticketCode ?? (r.ticketId ? `#${r.ticketId}` : '—')}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{duration(r.waitSeconds)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{duration(r.firstResponseSeconds)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{duration(r.effectiveSeconds)}</td>
                    {operatorId && <td className="px-3 py-2 text-right font-mono text-xs">{duration(r.operatorSeconds)}</td>}
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {r.aiUrgency && r.aiMood && (
                          <InsightBadges insight={{ attendanceId: r.id, urgency: r.aiUrgency, mood: r.aiMood, reason: r.aiReason, analyzedAt: '', everIrritated: r.aiEverIrritated }} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
